import React from 'react';
import ImageUploadField from './ImageUploadField';

export const CARD_SIZE_OPTIONS = [
  { value: 'square', label: 'Square — logo / small piece' },
  { value: 'wide', label: 'Wide — brochure / business cards' },
  { value: 'tall', label: 'Tall — banner / poster' },
  { value: 'large', label: 'Large — hero mockup' },
  { value: 'custom', label: 'Custom — px / in / cm / mm…' },
];

export const SIZE_UNIT_OPTIONS = [
  { value: 'px', label: 'Pixels (px)' },
  { value: 'in', label: 'Inches (in)' },
  { value: 'cm', label: 'Centimeters (cm)' },
  { value: 'mm', label: 'Millimeters (mm)' },
  { value: 'rem', label: 'Rem' },
  { value: '%', label: 'Percent (%)' },
];

const emptyGalleryCard = () => ({
  url: '',
  label: '',
  size: 'wide',
  colSpan: 6,
  rowSpan: 1,
  width: '',
  height: '',
  unit: 'px',
});

const emptyCustomField = () => ({
  label: '',
  value: '',
  type: 'text',
  size: 'wide',
  colSpan: 6,
  rowSpan: 1,
  width: '',
  height: '',
  unit: 'px',
});

function normalizeGalleryItems(value) {
  if (!Array.isArray(value) || value.length === 0) {
    return [emptyGalleryCard()];
  }

  return value.map((item) => {
    if (typeof item === 'string') {
      return { ...emptyGalleryCard(), url: item };
    }
    return {
      url: item?.url || item?.src || '',
      label: item?.label || '',
      size: item?.size || 'wide',
      colSpan: Number(item?.colSpan) > 0 ? Number(item.colSpan) : 6,
      rowSpan: Number(item?.rowSpan) > 0 ? Number(item.rowSpan) : 1,
      width: item?.width ?? '',
      height: item?.height ?? '',
      unit: item?.unit || 'px',
    };
  });
}

function SizeControls({ item, index, updateItem }) {
  return (
    <>
      <select
        value={item.size || 'wide'}
        onChange={(e) => updateItem(index, 'size', e.target.value)}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
      >
        {CARD_SIZE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {item.size === 'custom' ? (
        <div className="space-y-2 rounded-lg border border-primary/25 bg-primary/5 p-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
            Custom size (type aad rabto)
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="mb-1 block text-[11px] font-semibold text-slate-500 dark:text-gray-400">
                Width
              </label>
              <input
                type="number"
                min={0}
                step="any"
                value={item.width ?? ''}
                onChange={(e) => updateItem(index, 'width', e.target.value)}
                placeholder="e.g. 400"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold text-slate-500 dark:text-gray-400">
                Height
              </label>
              <input
                type="number"
                min={0}
                step="any"
                value={item.height ?? ''}
                onChange={(e) => updateItem(index, 'height', e.target.value)}
                placeholder="e.g. 300"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold text-slate-500 dark:text-gray-400">
                Unit / Type
              </label>
              <select
                value={item.unit || 'px'}
                onChange={(e) => updateItem(index, 'unit', e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
              >
                {SIZE_UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 dark:text-gray-500">
            Tusaale: 1080×1080 <strong>px</strong> = square card. Sawirka asalka ah haddii uu dheer/ballaran yahay, card-ku weli wuxuu haynayaa ratio-ga aad gelisay (object-cover).
          </p>
          {Number(item.width) > 0 && Number(item.height) > 0 ? (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-white/10 dark:bg-[#101010]">
              <div
                className="mx-auto w-full max-w-[220px] bg-slate-100 dark:bg-[#1a1a1a]"
                style={{ aspectRatio: `${Number(item.width)} / ${Number(item.height)}` }}
              >
                <div className="flex h-full items-center justify-center text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  Preview {item.width}×{item.height} {item.unit || 'px'}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export function ProjectGalleryField({ value = [], onChange }) {
  const items = normalizeGalleryItems(value);

  const updateItem = (index, field, nextValue) => {
    const nextItems = items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: nextValue } : item,
    );
    onChange(nextItems);
  };

  const addItem = () => onChange([...items, emptyGalleryCard()]);

  const removeItem = (index) => {
    const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
    onChange(nextItems.length > 0 ? nextItems : [emptyGalleryCard()]);
  };

  return (
    <div className="space-y-3">
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
        Branding / Gallery Cards
      </label>
      <p className="text-xs text-slate-400 dark:text-gray-500">
        Door preset ama <strong>Custom</strong> si aad u geliso width/height aad rabto.
      </p>
      {items.map((item, index) => (
        <div
          key={index}
          className="space-y-2 rounded-xl border border-slate-200 p-3 dark:border-white/10"
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
              Card {index + 1}
            </p>
            {items.length > 1 ? (
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="text-xs font-bold text-red-500"
              >
                Remove card
              </button>
            ) : null}
          </div>

          <input
            type="text"
            value={item.label || ''}
            onChange={(e) => updateItem(index, 'label', e.target.value)}
            placeholder="Label (Logo, Brochure, Packaging…)"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
          />

          <SizeControls item={item} index={index} updateItem={updateItem} />

          <ImageUploadField
            label="Mockup Image"
            value={item.url || ''}
            onChange={(url) => updateItem(index, 'url', url)}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-bold text-primary"
      >
        + Add New Card Mockup
      </button>
    </div>
  );
}

export function ProjectCustomFieldsField({ value = [], onChange }) {
  const items = Array.isArray(value) && value.length > 0 ? value : [emptyCustomField()];

  const updateItem = (index, field, nextValue) => {
    const nextItems = items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: nextValue } : item,
    );
    onChange(nextItems);
  };

  const addItem = () => onChange([...items, emptyCustomField()]);

  const removeItem = (index) => {
    const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
    onChange(nextItems.length > 0 ? nextItems : [emptyCustomField()]);
  };

  return (
    <div className="space-y-3">
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
        Extra Project Fields
      </label>
      <p className="text-xs text-slate-400 dark:text-gray-500">
        Add fields specific to this project type (e.g. Full Branding, Campaign URL, Analytics Dashboard).
      </p>
      {items.map((item, index) => (
        <div
          key={index}
          className="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 p-3 dark:border-white/10"
        >
          <input
            type="text"
            value={item.label || ''}
            onChange={(e) => updateItem(index, 'label', e.target.value)}
            placeholder="Field label (e.g. Full Branding)"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
          />
          <select
            value={item.type || 'text'}
            onChange={(e) => updateItem(index, 'type', e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
          >
            <option value="text">Text</option>
            <option value="url">URL</option>
            <option value="image">Image URL</option>
          </select>
          {item.type === 'image' ? (
            <>
              <SizeControls item={item} index={index} updateItem={updateItem} />
              <ImageUploadField
                label="Image"
                value={item.value || ''}
                onChange={(url) => updateItem(index, 'value', url)}
              />
            </>
          ) : (
            <input
              type="text"
              value={item.value || ''}
              onChange={(e) => updateItem(index, 'value', e.target.value)}
              placeholder={item.type === 'url' ? 'https://example.com' : 'Field value'}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
            />
          )}
          {items.length > 1 ? (
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="justify-self-start text-xs font-bold text-red-500"
            >
              Remove field
            </button>
          ) : null}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-bold text-primary"
      >
        + Add Field
      </button>
    </div>
  );
}
